import { Injectable, NgZone } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { IdexService } from 'app/idex.service';
import { AppLoaderComponent } from 'app/shared/app-loader/app-loader.component';
import { BehaviorSubject, Observable } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { Core, UI, WebViewerInstance } from '@pdftron/webviewer';

import { PDFTronIcons, setWebViewerDarkMode, setWebViewerLightMode, Theme, ThemeLabel, ThemeMode } from './services/change-theme-mode.service';
import { convertFileTypeToMimeType, convertMimeTypeToFileType } from './services/convert-mimeType.service';
import { promptDownloadDocDialog } from './services/download-doc.service';
import { insertFile } from './services/insert-file.service';
import { promptPrintConfirmDialog } from './services/print-doc.service';
import { setClearWatermarkOptions, setWatermarkOptions } from './services/set-watermark.service';
import * as moment from 'moment';
import { DocumentData } from './document-data.type';

@Injectable({ providedIn: "root" })
export class AnnotationControllerService {

   documentData: DocumentData;
   loader: AppLoaderComponent;
   _isOpened = false;
   private _isOpened$ = new BehaviorSubject<boolean>(false);
   constructor(
      private translate: TranslateService,
      private zone: NgZone,
      private dialog: MatDialog,
      private service: IdexService,
   ) {}

   //=============================================================================
   // PDFTRON'S WEBVIEWER INSTANCE
   //=============================================================================

   private webViewerInstance: WebViewerInstance;

   get instance() {
      return this.webViewerInstance;
   }
   set instance(instance: WebViewerInstance) {
      this.webViewerInstance = instance;
      this.updateNotesPanelVisibilityStatus();
   }

   //=============================================================================
   // THEME MODE
   //=============================================================================

   // Theme Mode: default is Light mode
   private currentThemeLabel: ThemeLabel = (this.isDarkMode())? ThemeLabel.LightMode : ThemeLabel.DarkMode;
   private currentThemeIcon: PDFTronIcons = (this.isDarkMode())? PDFTronIcons.DarkModeIcon : PDFTronIcons.LightModeIcon;
   private currentPrintIcon: PDFTronIcons = (this.isDarkMode())? PDFTronIcons.PrintDarkModeIcon : PDFTronIcons.PrintLightModeIcon;
   private currentDownloadIcon: PDFTronIcons = (this.isDarkMode())? PDFTronIcons.DownloadDarkModeIcon : PDFTronIcons.DownloadLightModeIcon;
   private currentSaveIcon: PDFTronIcons = (this.isDarkMode())? PDFTronIcons.SaveDarkModeIcon : PDFTronIcons.SaveLightModeIcon;
   private currentAddPageIcon: PDFTronIcons = (this.isDarkMode())? PDFTronIcons.AddPageDarkModeIcon : PDFTronIcons.AddPageLightModeIcon;
   private currentInsertFileIcon: PDFTronIcons = (this.isDarkMode())? PDFTronIcons.InsertFileDarkModeIcon : PDFTronIcons.InsertFileLightModeIcon;
   private currentSwitchViewIcon: PDFTronIcons = (this.isDarkMode())? PDFTronIcons.SwitchViewerDarkModeIcon : PDFTronIcons.SwitchViewerLightModeIcon;
   private currentAddTemplateIcon: PDFTronIcons = (this.isDarkMode())? PDFTronIcons.AddTemplateDarkModeIcon : PDFTronIcons.AddTemplateLightModeIcon;
   /** 
    * Variable to temporary store current theme before UI.setTheme() is called.
    * UI.setTheme() fires 'themeChanged' event BEFORE finishing changing the theme.
    * Thus, there's a potential loop of
    *     setThemeMode() -> UI.setTheme() -> 'themeChanged' event -> repeat
    *     // UI.setTheme() will never be finished.
    */
   private __currentTheme__;
   /**
    * Set theme mode of webviewer to light or dark.\
    * After themeMode is set, the new icon paths will also be automatically set.
    */
   setThemeMode(themeMode: ThemeMode) {
      //| This if-condition is to call setTheme only when the theme is really changed
      //| to avoid unnecessary firing event 'themeChanged'.
      //|
      //| After calling setTheme, the event 'themeChanged' will be fired every time
      //| even though the new theme is the same as the previous theme.
      //|_______________________________________________________
      if(this.getThemeMode(this.instance) !== themeMode && this.__currentTheme__ !== themeMode) {
         this.__currentTheme__ = themeMode;
         this.instance?.UI.setTheme(themeMode);
      }
      this.updateIconPathsForTheme(themeMode);
   }

   /**
    * Returns the current theme mode of PDFTron WebViewer.
    * @returns `ThemeMode.Light` or `ThemeMode.Dark`
    */
   getThemeMode(instance: WebViewerInstance): ThemeMode {
      if (!instance) {
         // Default: Light Mode
         return Theme.Light;
      }

      // I have tried all possible types, but no success. Type 'any' is the only way to go. 💩
      // See https://community.apryse.com/t/webviewer-how-to-get-current-theme-mode/7633/5
      return (instance.UI as any).selectors.getActiveTheme();
   }

   /**
    * This function updates the icon image paths for each button according to the currently selected theme mode.
    * 
    * It does **NOT** update the UI immediately; that must be done separately.
    * 
    * This function should be called whenever the theme mode is changed.
    * @param themeMode 
    */
   updateIconPathsForTheme(themeMode: ThemeMode) {
      if (themeMode === Theme.Light) {
         setWebViewerLightMode(this.instance);
         this.currentThemeLabel = ThemeLabel.DarkMode;
         this.currentThemeIcon = PDFTronIcons.LightModeIcon;
         this.currentPrintIcon = PDFTronIcons.PrintLightModeIcon;
         this.currentDownloadIcon = PDFTronIcons.DownloadLightModeIcon;
         this.currentSaveIcon = PDFTronIcons.SaveLightModeIcon;
         this.currentAddPageIcon = PDFTronIcons.AddPageLightModeIcon;
         this.currentInsertFileIcon = PDFTronIcons.InsertFileLightModeIcon;
         this.currentSwitchViewIcon = PDFTronIcons.SwitchViewerLightModeIcon;
         this.currentAddTemplateIcon = PDFTronIcons.AddTemplateLightModeIcon;
      } else {
         setWebViewerDarkMode(this.instance);
         this.currentThemeLabel = ThemeLabel.LightMode;
         this.currentThemeIcon = PDFTronIcons.DarkModeIcon;
         this.currentPrintIcon = PDFTronIcons.PrintDarkModeIcon;
         this.currentDownloadIcon = PDFTronIcons.DownloadDarkModeIcon;
         this.currentSaveIcon = PDFTronIcons.SaveDarkModeIcon;
         this.currentAddPageIcon = PDFTronIcons.AddPageDarkModeIcon;
         this.currentInsertFileIcon = PDFTronIcons.InsertFileDarkModeIcon;
         this.currentSwitchViewIcon = PDFTronIcons.SwitchViewerDarkModeIcon;
         this.currentAddTemplateIcon = PDFTronIcons.AddTemplateDarkModeIcon;
      }
   }

   get themeLabel() {
      return this.currentThemeLabel;
   }
   get themeIcon() {
      return this.currentThemeIcon;
   }
   get printIcon() {
      return this.currentPrintIcon;
   }
   get downloadIcon() {
      return this.currentDownloadIcon;
   }
   get saveIcon() {
      return this.currentSaveIcon;
   }
   get addPageIcon() {
      return this.currentAddPageIcon;
   }
   get insertFileIcon() {
      return this.currentInsertFileIcon;
   }
   get switchViewIcon() {
      return this.currentSwitchViewIcon;
   }
   get addTemplateIcon() {
      return this.currentAddTemplateIcon;
   }

   /**
    * Switch the current theme from Light to Dark or Dark to Light
    */
   switchThemeMode(): void {
      const oppositeTheme = this.getThemeMode(this.instance) === Theme.Light ? Theme.Dark : Theme.Light;
      this.setThemeMode(oppositeTheme);
   }

   isDarkMode(): boolean {
      return this.getThemeMode(this.instance) === Theme.Dark;
   }
   isLightMode(): boolean {
      return this.getThemeMode(this.instance) === Theme.Light;
   }

   //=============================================================================
   // FILE TYPE - MIME TYPE CONVERTER METHODS
   //=============================================================================

   mimeTypeFile(typeFile: string): string {
      return convertFileTypeToMimeType(typeFile);
   }

   checkMimeTypeSetFileName(mimeFile: string): string {
      return convertMimeTypeToFileType(mimeFile);
   }

   //=============================================================================
   // NOTESPANEL VISIBILITY OBSERVABLE
   //=============================================================================
   // NotesPanel คือฟีเจอร์จาก PDFTron คือแถบดูคอมเมนต์หรือโน้ต(ที่เป็น annotation) บนเอกสาร
   // สามารถเปิด-ปิด NotesPanel ได้ด้วยการคลิกปุ่ม Notes บนแถบเครื่องมือของ PDFTron
   // Observable นี้เอาไว้เพื่อให้ component อื่นดูได้ว่าแถบ NotesPanel ของ PDFTron ถูกเปิดอยู่
   // หรือเปล่า
   //=============================================================================

   private notesPanelVisibility = new BehaviorSubject<boolean>(false);
   private notesPanelVisibility$ = this.notesPanelVisibility.asObservable();
   
   /**
    * Return an Observable of PDFTron's NotesPanel visibility that can be subscribed.
    *
    * This Observable has two possible values:\
    * `True` = NotesPanel is currently visible on WebViewer.\
    * `False` = NotesPanel is currently NOT visible on WebViewer.
    *
    * This Observable emits a value every time PDFTron's NotesPanel button is toggled.
    * @returns Observable\<boolean>
    * @example ```
    * subscription = this.annotationControllerService.getNotesPanelVisibilityStatus().subscribe((isNotesPanelOpen) => {
    *     ...
    * });
    * ```
    */
   getNotesPanelVisibilityStatus(): Observable<boolean> {
      return this.notesPanelVisibility$;
   }

   updateNotesPanelVisibilityStatus() {
      if (this.instance?.UI.isElementOpen("notesPanel")) {
         this.notesPanelVisibility.next(true);
      } else {
         this.notesPanelVisibility.next(false);
      }
   }

   //=============================================================================
   // PRINT DOCUMENT
   //=============================================================================

   printDoc() {
      this.zone.run(() => {
         promptPrintConfirmDialog(
            this.instance,
            this.documentData,
            this.dialog,
            this.translate,
            this.loader
         );
      });
   }

   //=============================================================================
   // DOWNLOAD DOCUMENT
   //=============================================================================

   downloadDoc() {
      this.zone.run(() => {
         promptDownloadDocDialog(
            this.instance,
            this.documentData,
            this.dialog,
            this.translate
         );
      });
   }

   //=============================================================================
   // INCLUDE OR CLEAR WATERMARK WHEN PRINT OR DOWNLOAD DOCUMENT
   //=============================================================================

   setWatermarkOptions() {
      setWatermarkOptions(this.instance, this.documentData?.watermark);
   }

   setClearWatermarkOptions() {
      setClearWatermarkOptions(this.instance);
   }


   //=============================================================================
   // INSERT FILE
   //=============================================================================

   insertFile() {
      this.zone.run(async () => {
         insertFile(this.instance, this.dialog, this.service, this.loader);
      });
   }

   //=============================================================================
   // ROTATE SELECTED PAGES OR A CURRENT PAGE
   //=============================================================================

   rotatePage(action: 'clockwise'|'counterClockwise') {
      this.zone.run(() => {
         const rotation = this.instance?.Core.PageRotation;
         let selectedPage = this.instance?.UI.ThumbnailsPanel.getSelectedPageNumbers();
         const doc = this.instance?.Core.documentViewer.getDocument();

         if (selectedPage.length < 0) return;
         if (selectedPage.length === 0) {
            selectedPage = [this.instance?.Core.documentViewer.getCurrentPage()];
         }
         this.rotatingPage(action, selectedPage, doc, rotation);
      });
   }

   private rotatingPage(action: 'clockwise' | 'counterClockwise', selectedPage: number[], doc: Core.Document, rotation: typeof Core.PageRotation) {
      if (action === 'clockwise') {
         doc.rotatePages(selectedPage, rotation.E_90);
      } else {
         doc.rotatePages(selectedPage, rotation.E_270);
      }
   }
   getLang(name: string) {
      
      let title = '';
      if (name === 'SwitchView') {
         this.translate.get('SwitchView').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'SaveToPDF') {
         this.translate.get('SaveToPDF').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'AddPage') {
         this.translate.get('AddPage').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'InsertFile') {
         this.translate.get('InsertFile').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'Light Mode') {
         this.translate.get('LightMode').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'Dark Mode') {
         this.translate.get('DarkMode').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'Download') {
         this.translate.get('DownloadPdftron').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'Print') {
         this.translate.get('PrintPdftron').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'Notes') {
         this.translate.get('Notes').subscribe((s) => {
            title = s;
         });
      }
      if (name === 'AddTemplate') {
         this.translate.get('AddTemplate').subscribe((s) => {
            title = s;
         });
      }
      
      return title;
   }

   //=============================================================================
   // CONVERT BLOB TO FILE
   //=============================================================================

   blobToFile(theBlob: Blob, fileName: string) {
      return new File([theBlob], fileName, { lastModified: new Date().getTime(), type: theBlob.type });
   }

   //=============================================================================
   // SAVE FILE
   //=============================================================================
   // TODO: Refactor ย้ายโค้ดที่เกี่ยวกับการเซฟจาก component ให้มาอยู่ใน service ให้หมด
   // เพื่อให้การเซฟจากการกดปุ่มเซฟ และเซฟจากการกดปุ่มย้อนกลับ (save & quit) ใช้โค้ดเดียวร่วมกัน
   //=============================================================================

   /**
    * Generate file xml (xfdf) for saving annotation to our backend server
    * @param ins 
    * @returns 
    */
   async genFileXml(ins: WebViewerInstance) {
      if (!ins) return;

      const xmlString = await ins.Core.annotationManager.exportAnnotations();
      const blobs = new Blob([xmlString], { type: 'text/xml' });
      const date = moment(new Date());
      const formatDate = date.format('YYYYMMDDHHmmSS');
      const files = this.blobToFile(blobs, this.documentData.docId + '_annotation_' + formatDate + '.xml');
      return files;
   }
   
   /**
    * Generate xml (xfdf), then immediately merge with the document\
    * (special custom for our customer: EastWater)
    * @param ins 
    * @returns 
    */
   async genFileMerge(ins: WebViewerInstance) {
      if (!ins) return;

      const lastIndex = this.documentData.fileName.lastIndexOf('.');
      const fileNameWithoutExt = this.documentData.fileName.substring(0, lastIndex);
   
      const { SaveOptions } = ins.Core;
      const doc = ins.Core.documentViewer.getDocument();
      const xfdfString = await ins.Core.annotationManager.exportAnnotations();
      const saveOptions = SaveOptions;
      const options = { xfdfString, flags: saveOptions.REMOVE_UNUSED };
      const data = await doc.getFileData(options);
      const arr = new Uint8Array(data);
      const blob = new Blob([arr], { type: 'application/pdf' });
      const file = this.blobToFile(blob, fileNameWithoutExt + '.pdf');
      return file;
   }

   async genFileDoc(ins: WebViewerInstance, isCheckHideSaveBtnDoc: boolean, isRotated: boolean) {
      if (!ins) return;

      const lastIndex = this.documentData.fileName.lastIndexOf('.');
      const fileNameWithoutExt: string = this.documentData.fileName.substring(0, lastIndex);
      const subTypeFileName = this.documentData.fileName.split('.');
      const typeFile = subTypeFileName[subTypeFileName.length - 1];
      const doc = ins.Core.documentViewer.getDocument();
      const xfdfString = await ins.Core.annotationManager.exportAnnotationCommand();
      const options = { xfdfString };
      const data = await doc.getFileData(options);
      const arr = new Uint8Array(data);
      const mimeType = this.mimeTypeFile(typeFile);
      let blob: Blob;
      // กรณีถ้าเอกสารมีการ เพิ่มหรือลบหรือหมุนเอกสาร ไม่ว่ากรณีไหนก็จะ gen file เป็น pdf
      if (isCheckHideSaveBtnDoc || isRotated) {
         blob = new Blob([arr], { type: 'application/pdf' });
         this.documentData.mimeType = 'application/pdf';
      } else {
         // ถ้ามีแต่แก้ไขหรือเพิ่มลบ annotation อย่างเดียว
         blob = new Blob([arr], { type: '' + mimeType });
      }
      const lastType = this.checkMimeTypeSetFileName(this.documentData.mimeType);
      const file = this.blobToFile(blob, fileNameWithoutExt + '.' + lastType);
      return file;
   }
   
   setPdftronOpened(state: boolean) {
      this._isOpened$.next(state);
   }
   isPdftronOpened$(): Observable<boolean> {
      return this._isOpened$.asObservable();
   }

}
